import { useCallback, useEffect, useState } from "react"
import { Spinner } from "react-bootstrap"
import { finalize, map } from "rxjs/operators"
import { toast } from "react-toastify"
import { ClinicSearchAdditionalOptionsGetObservable, ClinicSearchAdditionalOptionsSetObservable } from "../../../api/Clinics/ClinicOptionsGet"
import ForcePrimaryClinicOptions from "./ForcePrimaryClinicOptions"
import FormatError from "../../../formatters/FormatError"
import { useEffectAfterMount } from "../../../utils/hooks"

export interface AdditionalOptionsProps {
  isPortal: boolean
  update: boolean
  setSaving: (saving: boolean) => void
}

export enum AdditionalOptionsType {
  FORCE_PRIMARY_CLINIC = 1
}

export interface AdditionalOption {
  OptionTypeID: AdditionalOptionsType
  OptionValue: string
}

const AdditionalOptions = (props: AdditionalOptionsProps) => {
  const { setSaving, update } = props
  const [loading, setLoading] = useState(false)
  const [options, setOptions] = useState<AdditionalOption[]>([])

  useEffect(() => {
    setLoading(true)
    const sub = ClinicSearchAdditionalOptionsGetObservable()
      .pipe(
        map(options => options.map(
          option => ({
            OptionTypeID: option.ClinicSearchOptionTypeID,
            OptionValue: option.Value
          })
        )),
        finalize(() => setLoading(false))
      )
      .subscribe(
        data => setOptions(data),
        error => toast.error(FormatError(error))
      )
    return () => sub.unsubscribe()
  }, [])

  useEffectAfterMount(() => {
    setSaving(true)
    const sub = ClinicSearchAdditionalOptionsSetObservable(options)
      .pipe(finalize(() => setSaving(false)))
      .subscribe(
        data => toast.success(data),
        error => toast.error(FormatError(error))
      )
    return () => sub.unsubscribe()
  }, [update, setSaving])

  const onUpdateOption = useCallback((optionType: AdditionalOptionsType, value: string) => {
    setOptions(prev => prev.map(obj => {
      return obj.OptionTypeID === optionType
        ? { ...obj, OptionValue: value }
        : obj
    }))
  }, [])

  const getOptionValue = useCallback((optionType: AdditionalOptionsType) => {
    return options.find(o => o.OptionTypeID === optionType)?.OptionValue
  }, [options])

  return (
    <>
      {loading && <Spinner animation="border" />}
      {!loading && props.isPortal && <ForcePrimaryClinicOptions
        selected={getOptionValue(AdditionalOptionsType.FORCE_PRIMARY_CLINIC)}
        onUpdate={onUpdateOption}
      />}
    </>
  )
}

export default AdditionalOptions
